import {
  AccountingSystem,
  CreateAccountingSystemBillInput,
} from '../API';
import { AccountingSystemConfigValues } from '../routes/create-organization/accounting-system-config';

/**
 * Accounting systems that support per-employee reimbursement
 * (drives both the wizard toggle visibility and the submit-time coercion).
 */
export const ACCOUNTING_SYSTEMS_WITH_REIMBURSEMENT: AccountingSystem[] = [
  AccountingSystem.bexio,
  AccountingSystem.topal,
];

export interface BuildAccountingSystemBillInputArgs {
  /** Wizard step state — null/undefined when the wizard hasn't reached the config step */
  accountingSystemConfigValues: AccountingSystemConfigValues | undefined;
  /** The selected accounting system from bookkeepingValues */
  accountingSystem: AccountingSystem | undefined;
  /** Bexio user ID from /3.0/users/me — defined ONLY for Bexio path */
  contactPartnerId: number | undefined;
  /** Freshly generated guid() for this UserBookkeeping */
  userBookkeepingId: string;
  /** User Cognito sub */
  authUserId: string;
  /** Onboarding-scope auth groups */
  authGroups: string[];
}

/**
 * Returns a CreateAccountingSystemBillInput when a bill should be created, or null when it shouldn't.
 *
 * Hybrid predicate (D-06, refined per MR review CR-01):
 *   - Bexio: create only when contactPartnerId is set (preserves Bexio sync at
 *     expenslyAccountingBexioTransactionSync/src/index.mjs:290-291). Bexio + reimbursement=true
 *     without contactPartnerId returns null — the Bexio API path is expected to populate cpid;
 *     missing cpid signals an upstream error (Bexio API timeout / user not yet in Bexio) and
 *     creating an orphan bill without cpid would break Bexio sync.
 *   - Topal + reimbursement=true: create WITHOUT contactPartnerId.
 *   - Topal + reimbursement=false: skip (returns null).
 *   - banana / abacus / none: skip.
 *
 * contactPartnerId is omitted entirely from the returned input when undefined (D-09).
 *
 * Note: ACCOUNTING_SYSTEMS_WITH_REIMBURSEMENT (bexio + topal) drives UI-toggle visibility
 * and submit-time coercion, but the bill-creation predicate here is Topal-only — see CR-01.
 */
export function buildAccountingSystemBillInput(
  args: BuildAccountingSystemBillInputArgs,
): CreateAccountingSystemBillInput | null {
  const {
    accountingSystemConfigValues,
    accountingSystem,
    contactPartnerId,
    userBookkeepingId,
    authUserId,
    authGroups,
  } = args;

  if (!accountingSystemConfigValues || !accountingSystem) return null;

  const shouldCreate =
    Boolean(contactPartnerId) ||
    (accountingSystem === AccountingSystem.topal &&
      accountingSystemConfigValues.reimbursement === true);

  if (!shouldCreate) return null;

  // Wizard validation guarantees senderBankAccountId is set before reaching this point.
  // If undefined here, surfacing it loudly (throw) is better than silently skipping
  // bill creation — silent skip would leave the org without a bill and no error trail.
  if (accountingSystemConfigValues.senderBankAccountId === undefined) {
    throw new Error(
      'buildAccountingSystemBillInput: senderBankAccountId is undefined but bill creation was triggered. ' +
        'This is a wizard-validation bug — the config step must require senderBankAccountId before submit.',
    );
  }

  return {
    ...(contactPartnerId !== undefined && { contactPartnerId }),
    senderBankAccountId: accountingSystemConfigValues.senderBankAccountId,
    billingPeriod: accountingSystemConfigValues.billingPeriod,
    baseCurrency: accountingSystemConfigValues.baseCurrency,
    userBookkeepingId,
    authUserId,
    authGroups,
  };
}
